/**
 * Users API Service
 *
 * Handles all user management API calls.
 * Matches FastAPI backend snake_case conventions.
 */

import { api } from '../client';

export const usersService = {
  /**
   * Get all users
   * @returns {Promise<Array>} Array of users
   */
  async getAll() {
    const response = await api.post('/api/user', {});
    return response || [];
  },

  /**
   * Get a single user by ID
   * @param {string} userId - User ID
   * @returns {Promise<Object>} User details
   */
  async getById(userId) {
    const response = await api.post('/api/user', { _id: userId });
    return response[0]; // API returns array
  },

  /**
   * Get current user info
   * @returns {Promise<Object>} Current user
   */
  async getMe() {
    return await api.get('/user/me');
  },

  /**
   * Update the current user's own profile (self-service, non-admin).
   * Only self-safe fields are accepted by the backend (e.g. preferred_language).
   * @param {Object} updates - Fields to update, e.g. { preferred_language: 'de' }
   * @returns {Promise<Object>} Updated current user
   */
  async updateMe(updates) {
    return await api.patch('/api/user/me', updates);
  },

  /**
   * Create a new user
   * @param {Object} userData - User data (is_admin, project_ids, etc.)
   * @returns {Promise<Object>} Created user
   */
  async create(userData) {
    return await api.put('/api/user', userData);
  },

  /**
   * Update an existing user
   * @param {string} userId - User ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated user
   */
  async update(userId, updates) {
    return await api.patch('/api/user', {
      query: { _id: userId },
      update_data: updates,
    });
  },

  /**
   * Delete a user
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Deletion response
   */
  async delete(userId) {
    return await api.delete('/api/user', {
      data: { _id: userId },
    });
  },

  /**
   * Get users by project
   * @param {string} projectId - Project ID
   * @returns {Promise<Array>} Array of users for project
   */
  async getByProject(projectId) {
    const response = await api.post('/api/user', { project_ids: projectId });
    return response || [];
  },
};
